import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useLanguage } from '@/hooks/use-language';
import { usePenalty } from '@/hooks/use-penalty';
import padelHero from '@/assets/padel-hero.jpg';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { PenaltyNotice } from '@/components/ui/penalty-notice';
import { Users, Calendar, Settings, Clock, MapPin } from 'lucide-react';
import { getWeekNumber, isNewWeek, formatEventTime, isEventInPast, getRelativeDateLabel, isInCurrentSemester } from '@/utils/dateUtils';
import { useEvents } from '@/hooks/use-events';
import { useAdminStats } from '@/hooks/use-admin-stats';
import { cn } from '@/lib/utils';
import { getGroupColorClasses, getLevelGroupName } from '@/utils/groupColors';

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { getMemberPenalty } = usePenalty();
  const navigate = useNavigate();
  const [actionedEvents, setActionedEvents] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  const userPenalty = user ? getMemberPenalty(user.id) : null;

  // Helper functions for date formatting
  const formatMonth = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short' });
  };

  const formatDay = (date: Date) => {
    return date.getDate().toString();
  };

  const formatWeekday = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const formatEventDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const { events, signUp, cancel, joinWaitingList, leaveWaitingList } = useEvents();
  const [showPastEvents, setShowPastEvents] = useState(false);
  const adminStats = useAdminStats();

  const handleMembersClick = () => {
    navigate('/members');
  };

  const handleSubgroupsClick = () => {
    navigate('/subgroups');
  };

  const handleAdminClick = () => {
    navigate('/admin');
  };

  // Handle signup/waitlist actions with optimistic UI update
  const handleEventAction = (eventId: string, action: () => void) => {
    // Immediately update UI (optimistic)
    setActionedEvents(prev => new Set(prev).add(eventId));
    
    try {
      action();
      // Auto-clear after 3 seconds on success
      setTimeout(() => {
        setActionedEvents(prev => {
          const newSet = new Set(prev);
          newSet.delete(eventId);
          return newSet;
        });
      }, 3000);
    } catch (error) {
      // Revert UI change on error
      setActionedEvents(prev => {
        const newSet = new Set(prev);
        newSet.delete(eventId);
        return newSet;
      });
    }
  };

  // Handle tournament navigation with scroll to top
  const handleTournamentClick = (eventId: string) => {
    // Scroll to top before navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Small delay to allow scroll to complete
    setTimeout(() => {
      navigate(`/tournament/${eventId}`);
    }, 300);
  };

  // Handle event detail navigation with scroll to top
  const handleEventClick = (eventId: string) => {
    // Scroll to top before navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Small delay to allow scroll to complete
    setTimeout(() => {
      navigate(`/event/${eventId}`);
    }, 300);
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const eventsToRender = React.useMemo(() => {
    const now = new Date();
    
    if (showPastEvents) {
      // Show ONLY past events (more than 1 hour after they ended)
      const pastEvents = events.filter(event => {
        const eventDateTime = new Date(event.date);
        const [hours, minutes] = event.time.split(':').map(Number);
        eventDateTime.setHours(hours, minutes, 0, 0);
        const eventEndTime = new Date(eventDateTime.getTime() + 60 * 60 * 1000);
        
        // Filter by: event ended AND within current semester
        return eventEndTime < now && isInCurrentSemester(event.date);
      });
      
      // Sort DESCENDING (newest first) for past events
      return pastEvents.sort((a, b) => b.date.getTime() - a.date.getTime());
    }
    
    // Show only upcoming events (1 hour buffer after they finish)
    const upcomingEvents = events.filter(event => {
      const eventDateTime = new Date(event.date);
      const [hours, minutes] = event.time.split(':').map(Number);
      eventDateTime.setHours(hours, minutes, 0, 0);
      const eventEndTime = new Date(eventDateTime.getTime() + 60 * 60 * 1000);
      
      // Filter by: event not ended yet AND within current semester
      return eventEndTime >= now && isInCurrentSemester(event.date);
    });
    
    // Sort ASCENDING (soonest first) for upcoming events
    return upcomingEvents.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [events, showPastEvents]);

  const occupancyStats = React.useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const upcoming = events.filter(event => event.date.getTime() >= now.getTime());

    if (!upcoming.length) {
      return {
        totalEvents: 0,
        totalSlots: 0,
        totalRegistrations: 0,
        waitlistCount: 0,
        fullEvents: 0,
        highDemandEvents: [] as {
          id: string;
          name: string;
          occupancy: number;
          waitlist: number;
        }[],
      };
    }

    const totalSlots = upcoming.reduce((sum, event) => sum + (event.maxParticipants || 0), 0);
    const totalRegistrations = upcoming.reduce((sum, event) => sum + (event.participants?.length || 0), 0);
    const waitlistCount = upcoming.reduce((sum, event) => sum + ((event.waitingList || []).length), 0);
    const fullEvents = upcoming.filter(event => (event.participants?.length || 0) >= event.maxParticipants).length;

    const highDemandEvents = upcoming
      .map(event => {
        const participants = event.participants?.length || 0;
        const capacity = event.maxParticipants || 0;
        const occupancy = capacity > 0 ? Math.round((participants / capacity) * 100) : 0;
        const waitlist = event.waitingList?.length || 0;
        return {
          id: event.id,
          name: event.title,
          occupancy,
          waitlist,
        };
      })
      .filter(item => item.waitlist > 0 || item.occupancy >= 75)
      .sort((a, b) => b.occupancy - a.occupancy)
      .slice(0, 3);

    return {
      totalEvents: upcoming.length,
      totalSlots,
      totalRegistrations,
      waitlistCount,
      fullEvents,
      highDemandEvents,
    };
  }, [events]);

  const noEventsMessage = showPastEvents
    ? t('dashboard.no_events_available') || 'No events available in this range'
    : t('dashboard.no_upcoming_events') || 'No upcoming events right now. Try showing past events.';

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Padel Tennis-inspired cover photo */}
      <div className="relative h-48 bg-gradient-to-r from-green-600 to-green-800 rounded-lg overflow-hidden">
        <img 
          src={padelHero}
          alt="Padel tennis court"
          className="w-full h-full object-cover opacity-80 transition-opacity duration-300"
          loading="lazy"
          decoding="async"
          onLoad={(e) => e.currentTarget.classList.add('opacity-80')}
          onError={(e) => e.currentTarget.style.display = 'none'}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        <div className="absolute bottom-4 left-4 text-white">
          <div className="space-y-3">
            {user?.isAdmin && (
              <div className="flex">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={handleAdminClick}
                  className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {t("nav.admin")}
                </Button>
              </div>
            )}
            <div className="flex gap-4">
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handleMembersClick}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <Users className="h-4 w-4 mr-2" />
                {adminStats?.totalUsers || 0} {t("welcome.members")}
              </Button>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handleSubgroupsClick}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <Users className="h-4 w-4 mr-2" />
                {adminStats?.totalGroups || 0} {t("welcome.subgroups")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {t("dashboard.title")}
          </h2>
          <p className="text-muted-foreground mt-2">
            {t("dashboard.subtitle")}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowPastEvents(prev => !prev)}
        >
          {showPastEvents
            ? t('dashboard.hide_past_events') || 'Hide past events'
            : t('dashboard.show_past_events') || 'Show past events'}
        </Button>
      </div>


      {/* Colorful Events List */}
      <div className="space-y-4">
        {eventsToRender.length === 0 && (
          <Card className="bg-muted/20">
            <CardContent className="py-10 text-center text-muted-foreground">
              {noEventsMessage}
            </CardContent>
          </Card>
        )}
        {eventsToRender.map((event, index) => {
          const previousEvent = index > 0 ? eventsToRender[index - 1] : null;
          const showWeekSeparator = isNewWeek(event.date, previousEvent?.date || null);
          const weekNumber = getWeekNumber(event.date);
          
          // Use the actual group name from the database instead of level-based name
          const displayGroupLabel = event.groupKey ? t(event.groupKey) : event.group;
          
          // Use database color if available, otherwise fall back to hardcoded colors
          const useDatabaseColor = !!event.groupColor;
          const colorClasses = useDatabaseColor 
            ? { bg: '', text: '', variant: 'custom' }
            : getGroupColorClasses(event.group || '');
          
          const cardBorderStyle = useDatabaseColor
            ? { borderLeftColor: event.groupColor }
            : (colorClasses.variant === 'secondary'
                ? undefined
                : { borderLeftColor: `hsl(var(--group-${colorClasses.variant}))` });
          
          const badgeStyle = useDatabaseColor
            ? { backgroundColor: event.groupColor, color: '#ffffff' }
            : undefined;
          
          const badgeClassName = useDatabaseColor
            ? ''
            : `${colorClasses.bg} ${colorClasses.text}`;
          
          const isActioned = actionedEvents.has(event.id);
          const isPastEvent = isEventInPast(event.date, event.time);
          
          return (
            <div key={event.id}>
              {showWeekSeparator && (
                <div className="flex items-center gap-3 mb-4">
                  <h3 className="text-sm font-medium text-gray-500">
                    {showPastEvents 
                      ? (getRelativeDateLabel(event.date) || `${t("dashboard.week")} ${weekNumber}`)
                      : `${t("dashboard.week")} ${weekNumber}`
                    }
                  </h3>
                  <Separator className="flex-1" />
                </div>
              )}
              
              <Card 
                className="overflow-hidden border-l-4 border-l-transparent hover:bg-card-hover transition-colors cursor-pointer hover:shadow-md"
                style={cardBorderStyle}
                title={event.type === 'tournament' ? 'Click to view tournament details' : 'Click to view event details and participants'}
                onClick={(e) => {
                  e.stopPropagation();
                  if (event.type === 'tournament') {
                    handleTournamentClick(event.id);
                  } else {
                    handleEventClick(event.id);
                  }
                }}
              >
                <CardContent className="p-0">
                  <div className="flex">
                    {/* Date Section */}
                    <div 
                      className={cn("p-4 flex flex-col items-center justify-center min-w-[80px]", badgeClassName)}
                      style={badgeStyle}
                    >
                      <div className="text-xs font-medium">{formatWeekday(event.date)}</div>
                      <div className="text-2xl font-bold">{formatDay(event.date)}</div>
                      <div className="text-xs font-medium">{formatMonth(event.date)}</div>
                      <div className="text-sm font-bold mt-3">{formatEventTime(event.time)}</div>
                    </div>
                    
                    {/* Content Section */}
                    <div className="flex-1 p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg">
                              {event.titleKey ? t(event.titleKey) : event.title}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {event.descriptionKey ? t(event.descriptionKey) : event.description}
                            </p>
                          </div>
                          {displayGroupLabel && (
                            <Badge
                              variant="outline"
                              className={cn("text-xs border-transparent", badgeClassName)}
                              style={badgeStyle}
                            >
                              {displayGroupLabel}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Event Details with Icons */}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatEventDate(event.date)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatEventTime(event.time)}
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {event.location}
                          </div>
                        </div>
                        
                        <div className="text-sm font-medium">
                          {event.participants.length}/{event.maxParticipants} {t("dashboard.participants")}
                          {(event.waitingList?.length || 0) > 0 && (
                            <span className="text-orange-600 ml-2">
                              {t("event.waitlist.status", { count: event.waitingList?.length || 0 })}
                            </span>
                          )}
                        </div>

                        {/* Penalty Notice */}
                        {userPenalty && (
                          <PenaltyNotice penalty={userPenalty} className="mt-3" />
                        )}

                        <div className="mt-3">
                          {event.participants.includes(user?.id || "") ? (
                            <div className="space-y-2">
                              {!isPastEvent ? (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEventAction(event.id, () => cancel(event.id, user!.id));
                                    }}
                                    className="bg-red-500 hover:bg-red-600 text-white"
                                  >
                                    {t("event.cancel.button")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (event.type === 'tournament') {
                                        handleTournamentClick(event.id);
                                      } else {
                                        handleEventClick(event.id);
                                      }
                                    }}
                                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                  >
                                    More Details
                                  </Button>
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground">
                                  {t("event.past") || "This event has already occurred"}
                                </div>
                              )}
                            </div>
                          ) : (event.waitingList || []).includes(user?.id || "") ? (
                            <div className="space-y-2">
                              {!isPastEvent ? (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEventAction(event.id, () => leaveWaitingList(event.id, user!.id));
                                    }}
                                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                  >
                                    {t("event.waitlist.leave")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (event.type === 'tournament') {
                                        handleTournamentClick(event.id);
                                      } else {
                                        handleEventClick(event.id);
                                      }
                                    }}
                                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                  >
                                    More Details
                                  </Button>
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground">
                                  {t("event.past") || "This event has already occurred"}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {event.participants.length < event.maxParticipants ? (
                                <div className="space-y-2">
                                  {!isPastEvent ? (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEventAction(event.id, () => signUp(event.id, user!.id));
                                        }}
                                        className="bg-green-500 hover:bg-green-600 text-white"
                                      >
                                        {t("event.signup.button")}
                                      </Button>
                                      {isActioned && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (event.type === 'tournament') {
                                              handleTournamentClick(event.id);
                                            } else {
                                              handleEventClick(event.id);
                                            }
                                          }}
                                          className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                        >
                                          More Details
                                        </Button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-muted-foreground">
                                      {t("event.past")}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {!isPastEvent ? (
                                    <>
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEventAction(event.id, () => joinWaitingList(event.id, user!.id));
                                          }}
                                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                                        >
                                          {t("event.waitlist.join")}
                                        </Button>
                                        {isActioned && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (event.type === 'tournament') {
                                                handleTournamentClick(event.id);
                                              } else {
                                                handleEventClick(event.id);
                                              }
                                            }}
                                            className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                          >
                                            More Details
                                          </Button>
                                        )}
                                      </div>
                                      <p className="text-xs text-orange-600">
                                        {t("event.waitlist.full")}
                                      </p>
                                    </>
                                  ) : (
                                    <div className="text-sm text-muted-foreground">
                                      {t("event.past") || "This event has already occurred"}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Notes */}
                        {event.notes && (
                          <p className="text-sm text-gray-500 italic mt-2">{event.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}
